<script lang="ts">
  import { projectStore } from '$lib/state/project.svelte';
  import { computeStatus } from '$lib/compute/recompute.svelte';
  import { walkLeaves } from '$lib/model/tree';
  import { computeTiles } from '$lib/compute/tiling';
  import { buildRecipePayload, encodeRecipe } from '$lib/io/recipe';

  interface Props {
    onMessage?: (m: { kind: 'warn' | 'error' | 'info'; text: string }) => void;
  }
  const { onMessage }: Props = $props();

  let running = $state(false);

  const enabledLeafCount = $derived.by(() => {
    const leaves = walkLeaves(projectStore.pipeline);
    return leaves.filter((l) => l.preview?.enabled !== false).length;
  });
  const tileGrid = $derived(computeTiles(projectStore.settings));
  const totalFiles = $derived(enabledLeafCount * tileGrid.tiles.length);

  async function doExport() {
    if (running) return;
    if (enabledLeafCount === 0) {
      onMessage?.({
        kind: 'error',
        text: 'No enabled layers to export.',
      });
      return;
    }
    running = true;
    try {
      // Lazy-load the export pathway: JSZip + the encoder pull in ~50 KB gzip
      // we don't need on first paint. Dynamic import keeps the main bundle
      // small for visitors who never click Export.
      const { buildExportZip, ExportError: _ExportError } = await import(
        '$lib/io/export-build'
      );
      void _ExportError;

      const outputsByStepId = new Map<string, ImageData[]>(
        computeStatus.outputs.map((o) => [o.stepId, o.images]),
      );
      const settingsSnap = $state.snapshot(projectStore.settings);
      const pipelineSnap = $state.snapshot(projectStore.pipeline);
      const layerOrderSnap = $state.snapshot(projectStore.layer_order);

      const recipePayload = buildRecipePayload(
        settingsSnap,
        pipelineSnap,
        layerOrderSnap,
      );
      const encoded = await encodeRecipe(recipePayload);

      const result = await buildExportZip({
        project_name: projectStore.name,
        settings: settingsSnap,
        pipeline: pipelineSnap,
        layer_order: layerOrderSnap,
        outputsByStepId,
        inputFilename: projectStore.input_asset?.filename ?? 'untitled',
        inputMime: projectStore.input_asset?.mime ?? 'image/png',
        inputSourceW: projectStore.input_asset?.source_width ?? 0,
        inputSourceH: projectStore.input_asset?.source_height ?? 0,
        inputSourceDpi: projectStore.input_asset?.source_dpi ?? 72,
        recipeText: encoded.text,
        recipeId: encoded.id,
      });
      triggerDownload(result.blob, result.filename);
      onMessage?.({
        kind: 'info',
        text: `Exported ${result.fileCount} file${result.fileCount === 1 ? '' : 's'} to ${result.filename}.`,
      });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Export failed.';
      onMessage?.({ kind: 'error', text: msg });
    } finally {
      running = false;
    }
  }

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
</script>

<button
  class="export"
  onclick={doExport}
  disabled={running || enabledLeafCount === 0}
  title="Export transparencies as a ZIP"
>
  {#if running}exporting…{:else}Export ↓{/if}
  {#if enabledLeafCount > 0 && !running}
    <span class="count">
      {enabledLeafCount} layer{enabledLeafCount === 1 ? '' : 's'}
      {#if tileGrid.tiles.length > 1}
        × {tileGrid.tiles.length} tiles
      {/if}
    </span>
  {/if}
</button>

<style>
  .export {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--accent-dim);
    color: var(--bg);
    border-color: var(--accent);
    padding: 6px 14px;
    font-weight: 600;
  }
  .export:hover:not(:disabled) {
    background: var(--accent);
  }
  .export:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .count {
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 400;
    opacity: 0.8;
  }
</style>
